import styled from "@emotion/styled";
import { Search } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import {
  BackgroundInputField,
  Button,
  Loading,
  Notification
} from "components/UI";
import Fuse from "fuse.js";
import { isEmpty, isNil, map } from "lodash";
import { useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCentralStore } from "stores";
import { useDenominations } from "../api";
import { DenominationThumbnail } from "../components";
import { useDenominationStore } from "../store";

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 2rem 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 1.5rem 1rem;
    padding: 0.5rem;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 2.5rem 2rem;
  }
`;

export function Denominations() {
  const denominationsQuery = useDenominations();
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const selectDenomination = useDenominationStore(
    state => state.selectDenomination
  );
  const authUser = useAuthUser();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [denominationsList, setDenominationsList] = useState([]);
  const [filteredDenominationsList, setFilteredDenominationsList] = useState(
    []
  );
  const { t } = useTranslation();

  useEffect(() => {
    setHeaderTitle("");

    return () => setHeaderTitle("");
  }, []);

  useEffect(() => {
    selectDenomination(null);
  }, []);

  useEffect(() => {
    if (denominationsQuery.isSuccess) {
      setDenominationsList(denominationsQuery.data.result);
    }

    return () => setDenominationsList([]);
  }, [denominationsQuery.isSuccess, denominationsQuery.data]);

  useEffect(() => {
    if (denominationsQuery.isSuccess) {
      setFilteredDenominationsList(denominationsQuery.data.result);
    }

    return () => setFilteredDenominationsList([]);
  }, [denominationsQuery.isSuccess, denominationsQuery.data]);

  const searchDenomination = () => {
    const fuse = new Fuse(denominationsList, {
      threshold: 0.3,
      keys: ["name"]
    });

    if (isEmpty(searchTerm)) {
      setFilteredDenominationsList(denominationsList);
      return;
    }

    const searchResults = fuse.search(searchTerm);

    if (isEmpty(searchResults)) {
      setFilteredDenominationsList([]);
    } else {
      setFilteredDenominationsList([
        ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
      ]);
    }
  };

  return (
    <Box py={{ xs: 3, md: 4, lg: 6 }} sx={{ my: "auto" }}>
      <Notification />
      <Stack
        alignItems="center"
        textAlign="center"
        spacing={{ xs: 3, md: 4 }}
        px={{ xs: 2, sm: 4, lg: 8 }}
      >
        <Stack spacing={{ xs: 2, md: 3 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: { xs: "1rem", md: "1.1rem" } }}
          >
            {t("denominations.denominations.welcome")} {authUser()?.first_name}{" "}
            {authUser()?.last_name}
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: "600px", mx: "auto" }}>
            {t("denominations.denominations.welcomeDescription")}
          </Typography>
          <Stack
            justifyContent="center"
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ mt: 2 }}
          >
            <BackgroundInputField
              placeholder={t("denominations.denominations.searchDenomination")}
              sx={{
                backgroundColor: "neutral.main",
                width: { xs: "100%", sm: "350px", lg: "400px" }
              }}
              neutral={1}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyUp={searchDenomination}
              inputProps={{ "aria-label": "search" }}
              autoComplete="off"
            />
            <Button
              variant="contained"
              endIcon={<Search />}
              onClick={searchDenomination}
              sx={{ minWidth: { xs: "100%", sm: "auto" } }}
            >
              {t("denominations.denominations.search")}
            </Button>
          </Stack>
          <Typography sx={{ fontWeight: 500, mt: 2 }}>
            {t("denominations.denominations.selectDescription")}
          </Typography>
        </Stack>
        {denominationsQuery.isLoading ? (
          <Loading />
        ) : filteredDenominationsList.length === 0 ? (
          <Box sx={{ py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? t("denominations.denominations.noSearchResults") ||
                  "No denominations found matching your search."
                : t("denominations.denominations.noDenominations") ||
                  "No denominations available."}
            </Typography>
          </Box>
        ) : (
          <GridWrapper>
            {filteredDenominationsList.map(denomination => (
              <DenominationThumbnail
                key={denomination.id}
                id={denomination.id}
                image={denomination.image_url}
                name={denomination.name}
                denomination={denomination}
                onClick={() => selectDenomination(denomination)}
              />
            ))}
          </GridWrapper>
        )}
        <Stack
          justifyContent="flex-end"
          sx={{ width: "100%" }}
          direction="row"
          spacing={2}
        >
          <Button
            size="large"
            variant="contained"
            flavor="secondary"
            onClick={() => navigate("/churches/create")}
          >
            {t("denominations.denominations.skip")}
          </Button>
          <Button
            size="large"
            variant="contained"
            flavor="primary"
            disabled={isNil(selectedDenomination)}
            onClick={() => navigate("/churches/create")}
          >
            {t("denominations.denominations.next")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
